import { TrackingOptions } from "../shared.modules";
import { deleteCookie, getCookie, setCookie } from "./cookies";
import { guid } from "./guid";
import {
  getLocalStorageWithExpiry,
  removeLocalStorageWithExpiry,
  setLocalStorageWithExpiry,
} from "./localStorageTemporal";

const COOKIE_SESSION_IDENTIFIER_SORT = "msm-identifier-session-sort";
const COOKIE_SESSION_IDENTIFIER_MEDIUM = "msm-identifier-session-medium";
const COOKIE_SESSION_IDENTIFIER_LONG = "msm-identifier-session-long";

/**
 * *This function generates unique session identifiers for short, medium, and long sessions lasting 1 hour, 1 day, and 30 days respectively.
 * @returns An object with a short, medium and long identifiers and a flag indicating if a new session has started.
 */
function sessionIdentifiers(): Partial<TrackingOptions> {
  const result = [
    getSessionIdentifierOrCreate(
      COOKIE_SESSION_IDENTIFIER_SORT,
      1000 * 60 * 60, // 1 hour
      true
    ),
    getSessionIdentifierOrCreate(
      COOKIE_SESSION_IDENTIFIER_MEDIUM,
      1000 * 60 * 60 * 24, // 1 day
      false
    ),
    getSessionIdentifierOrCreate(
      COOKIE_SESSION_IDENTIFIER_LONG,
      1000 * 60 * 60 * 24 * 30, // 30 days
      false
    ),
  ];

  return {
    sessionIdentifierShort: result[0].sessionIdentifier,
    sessionIdentifierMedium: result[1].sessionIdentifier,
    sessionIdentifierLong: result[2].sessionIdentifier,
    isSessionStarted:
      result[0].isSessionStarted ||
      result[1].isSessionStarted ||
      result[2].isSessionStarted,
  };
}

function clearSessions(): void {
  removeSessionIdentifier(COOKIE_SESSION_IDENTIFIER_SORT);
  removeSessionIdentifier(COOKIE_SESSION_IDENTIFIER_MEDIUM);
  removeSessionIdentifier(COOKIE_SESSION_IDENTIFIER_LONG);
}

function getSessionIdentifierOrCreate(
  name: string,
  ttlMs: number,
  autorefreshSession: boolean
): {
  sessionIdentifier: string;
  isSessionStarted: boolean;
} {
  let sessionIdentifier = getCookie(name);
  let isSessionStarted = false;

  if (!sessionIdentifier) {
    sessionIdentifier = getLocalStorageWithExpiry(name);
  }

  if (!sessionIdentifier) {
    sessionIdentifier = guid();
    isSessionStarted = true;

    setCookie({
      name: name,
      value: sessionIdentifier,
      expireInDays: ttlMs / (1000 * 60 * 60 * 24),
    });
    setLocalStorageWithExpiry(name, sessionIdentifier, ttlMs);
  } else if (autorefreshSession) {
    setCookie({
      name: name,
      value: sessionIdentifier,
      expireInDays: ttlMs / (1000 * 60 * 60 * 24),
    });
    setLocalStorageWithExpiry(name, sessionIdentifier, ttlMs);
  }

  return {
    sessionIdentifier,
    isSessionStarted,
  };
}

function removeSessionIdentifier(name: string) {
  deleteCookie(name);
  removeLocalStorageWithExpiry(name);
}

export { sessionIdentifiers, clearSessions };
